import { DataProvider } from "./DataProvider.js";

export class RpcDataProvider extends DataProvider {
    constructor(config) {
        super(config);
        this.config = {
            rpcMethod: 'getData', // Default list method
            createMethod: 'create', // Default create method
            updateMethod: 'update', // Default update method
            deleteMethod: 'delete', // Default delete method
            findByIdMethod: 'findById', // Default findById method
            ...this.config,
        };

        this.referenceCache = new Map();
        this.pendingResolutions = new Map();
        this.referenceOptionsCache = new Map();
        this.cachedFieldValues = new Map();
        if (!this.config.context) {
            this.config.context = {};
        }
        this.baseFilter = config.filter || {};

    }

    async loadData(params = {}) {
        try {
            this.log.info(`[${this.eventGroup}] Loading data with params:`, params);
            this.emit(`loading`);

            const finalFilter = { ...this.baseFilter, ...(params.filter || {}) };
            const methodToCall = `${this.config.rpcClass}.${this.config.rpcMethod}`;
            this.log.debug(`[${this.eventGroup}] Calling RPC method: ${methodToCall}`);

            const { data, totalCount } = await this.appClient.rpcClient.call(
                methodToCall,
                {
                    page: params.page || 1,
                    pageSize: this.config.pageSize,
                    filter: finalFilter,
                    sort: params.sort || {},
                },
                this.config.context
            );


            // Resolve references before setting data
            const resolvedData = await this.resolveReferences(data);

            this.data = resolvedData;
            this.totalCount = totalCount;

            this.emit(`dataLoaded`, {
                data: resolvedData,
                totalCount
            });

            return { data: resolvedData, totalCount };
        } catch (error) {
            if (error.name === 'Login' && !this.appClient.isLoggedIn()) {
                this.log.debug(`[${this.eventGroup}] RPC call blocked for logged-out user. Returning empty data.`);
                const emptyResult = { data: [], totalCount: 0 };
                this.data = [];
                this.totalCount = 0;
                this.emit(`dataLoaded`, emptyResult); // Still emit dataLoaded so UI can update to empty state
                return emptyResult;
            }

            // Gracefully handle missing plugins/RPC methods without crashing the UI
            if (error.message && (error.message.includes('not found') || error.message.includes('not properly decorated'))) {
                this.log.warn(`[${this.eventGroup}] Method not supported by target app: ${this.config.rpcClass}.${this.config.rpcMethod}. Suppressing error.`);
                const emptyResult = { data: [], totalCount: 0 };
                this.data = [];
                this.totalCount = 0;
                this.emit('methodNotSupported', { message: 'This feature is not supported or not installed on the target application.' });
                this.emit(`dataLoaded`, emptyResult); // Resolve the UI state gracefully
                return emptyResult;
            }

            this.log.error(`[${this.eventGroup}] Error loading data:`, error);
            this.emit(`dataError`, error);
            throw error; // Re-throw other errors

        }
    }

    async resolveReferences(data) {
        if (!Array.isArray(data) || !this.config.fields) {
            return data;
        }

        const referenceFields = Object.entries(this.config.fields)
            .filter(([_, config]) => config.reference)
            .map(([fieldName, config]) => ({
                fieldName,
                ...config.reference
            }));

        if (referenceFields.length === 0) {
            return data;
        }

        try {
            const resolvedData = [...data];

            await Promise.all(referenceFields.map(async (fieldConfig) => {
                const { fieldName, collection, valueField, displayField } = fieldConfig;

                // Get unique values to resolve
                const uniqueValues = [...new Set(
                    data
                        .map(item => item[fieldName])
                        .filter(value => value != null)
                )];

                if (uniqueValues.length === 0) return;

                // Check cache first
                const uncachedValues = uniqueValues.filter(
                    value => !this.referenceCache.has(`${collection}:${value}`)
                );

                if (uncachedValues.length > 0) {
                    // Fetch uncached references
                    const references = await this.appClient.rpcClient.call(
                        `${this.config.rpcClass}.getFieldValues`,
                        {
                            fieldName,
                            reference: fieldConfig
                        }
                    );

                    // Update cache
                    references.forEach(ref => {
                        this.referenceCache.set(
                            `${collection}:${ref.value}`,
                            ref.label
                        );
                    });
                }

                // Apply resolved values to data
                resolvedData.forEach(item => {
                    if (item[fieldName]) {
                        const cacheKey = `${collection}:${item[fieldName]}`;
                        item[`${fieldName}Display`] = this.referenceCache.get(cacheKey);
                    }
                });
            }));

            return resolvedData;
        } catch (error) {
            this.log.error('Error resolving references:', error);
            return data; // Return original data if resolution fails
        }
    }

    async getReferenceFieldOptions(fieldName, currentFormData = {}) {
        try {
            // This line is now correct because the provider was initialized with the full formConfig.fields
            const fieldConfig = this.config.fields[fieldName];
            if (!fieldConfig?.reference) {
                throw new Error(`No reference configuration found for field: ${fieldName}`);
            }

            // Create a dynamic cache key that includes the relevant dependency values
            let dynamicCacheKeyPart = '';
            if (fieldConfig.dependsOn && Array.isArray(fieldConfig.dependsOn)) {
                dynamicCacheKeyPart = fieldConfig.dependsOn
                    .map(dep => `${dep}=${currentFormData[dep] || ''}`)
                    .join('&');
            }
            const cacheKey = `options:${fieldConfig.reference.collection}:${dynamicCacheKeyPart}`;

            // Check the dynamic cache first
            if (this.referenceOptionsCache.has(cacheKey)) {
                return this.referenceOptionsCache.get(cacheKey);
            }

            // Prepare the reference object for the RPC call, resolving the filter if it's a function
            const referenceForRpc = { ...fieldConfig.reference };
            if (typeof referenceForRpc.filter === 'function') {
                // Pass the current form data to the filter function to get the dynamic filter object
                referenceForRpc.filter = referenceForRpc.filter(currentFormData);
            }

            // Fetch options from the server if not cached
            const options = await this.appClient.rpcClient.call(
                `${this.config.rpcClass}.getFieldValues`,
                {
                    fieldName,
                    reference: referenceForRpc // Pass the resolved reference object
                }
            );

            // Cache the options using the dynamic key
            this.referenceOptionsCache.set(cacheKey, options);

            return options;
        } catch (error) {
            if (error.name === 'Login' && !this.appClient.isLoggedIn()) {
                this.log.debug(`[${this.eventGroup}] RPC call for reference field '${fieldName}' blocked for logged-out user. Returning empty options.`);
                return []; // Return an empty array for dropdowns
            }
            this.log.error(`Error getting reference field options for ${fieldName}:`, error);
            throw error;
        }
    }


    clearCache() {
        this.referenceCache.clear();
        this.referenceOptionsCache.clear();

    }

    async getFieldValues(fieldName, config) {
        try {
            // Check cache first
            const cacheKey = `${fieldName}-${JSON.stringify(config)}`;
            if (this.cachedFieldValues.has(cacheKey)) {
                return this.cachedFieldValues.get(cacheKey);
            }

            this.log.info(`[${this.eventGroup}] Getting field values:`, {
                fieldName,
                config
            });

            const result = await this.appClient.rpcClient.call(
                `${this.config.rpcClass}.getFieldValues`,
                {
                    fieldName,
                    reference: config.reference,
                    context: this.config.context
                }
            );

            // Cache the result
            this.cachedFieldValues.set(cacheKey, result);
            return result;
        } catch (error) {
            if (error.name === 'Login' && !this.appClient.isLoggedIn()) {
                this.log.debug(`[${this.eventGroup}] RPC call for field values '${fieldName}' blocked for logged-out user. Returning empty options.`);
                return []; // Return an empty array
            }
            this.log.error(`Error getting field values for ${fieldName}:`, error);
            throw error;
        }
    }



    async insert(item) {
        try {
            const methodToCall = `${this.config.rpcClass}.${this.config.createMethod}`;
            const result = await this.appClient.rpcClient.call(
                methodToCall,
                item,
                this.config.context
            );
            this.emit(`dataInserted`, result);
            return result;
        } catch (error) {
            this.log.error("Error inserting item:", error);
            this.emit(`dataError`, error);
            throw error;
        }
    }

    async update(item) {
        try {
            const methodToCall = `${this.config.rpcClass}.${this.config.updateMethod}`;
            const result = await this.appClient.rpcClient.call(
                methodToCall,
                item,
                this.config.context
            );
            this.emit(`dataUpdated`, result);
            return result;
        } catch (error) {
            this.log.error("Error updating item:", error);
            this.emit(`dataError`, error);
            throw error;
        }
    }

    async delete(item) {
        try {
            const methodToCall = `${this.config.rpcClass}.${this.config.deleteMethod}`;
            const result = await this.appClient.rpcClient.call(
                methodToCall,
                { _id: item.id || item._id },
                this.config.context
            );
            this.emit(`dataDeleted`, result);
            return result;
        } catch (error) {
            this.log.error("Error deleting item:", error);
            this.emit(`dataError`, error);
            throw error;
        }
    }

    async findById(id) {
        try {
            this.log.info(`[${this.eventGroup}] Finding item by id:`, id);
            this.emit(`loading`);

            const methodToCall = `${this.config.rpcClass}.${this.config.findByIdMethod}`;
            const result = await this.appClient.rpcClient.call(
                methodToCall,
                { _id: id },
                this.config.context
            );
            return result;
        } catch (error) {
            this.log.error(`[${this.eventGroup}] Error finding item by id:`, error);
            this.emit(`dataError`, error);
            throw error;
        }
    }


    async getDataCount(filter) {
        try {
            return await this.appClient.rpcClient.call(
                `${this.config.rpcClass}.getDataCount`,
                filter,
                this.config.context
            );
        } catch (error) {
            this.log.error("Error getting data count:", error);
            this.emit(`dataError`, error);
            throw error;
        }
    }

    async getNextSetOfData(startIndex, count, filter) {
        try {
            return await this.appClient.rpcClient.call(
                `${this.config.rpcClass}.getNextSetOfData`,
                { startIndex, count, filter },
                this.config.context
            );
        } catch (error) {
            this.log.error("Error getting next set of data:", error);
            this.emit(`dataError`, error);
            throw error;
        }
    }

    processFilter(filter) {
        if (!filter) return null;

        // Convert filter values as needed (e.g., dates to ISO strings)
        const processed = {};
        Object.entries(filter).forEach(([key, value]) => {
            if (value instanceof Date) {
                processed[key] = value.toISOString();
            } else if (Array.isArray(value) && value.length === 0) {
                // Skip empty arrays
                return;
            } else if (value === '' || value === null || value === undefined) {
                // Skip empty values
                return;
            } else {
                processed[key] = value;
            }
        });

        return processed;
    }

    setContext(context) {
        this.config.context = context;
    }

    destroy() {
        this.clearCache();
        super.destroy();
    }
}